import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

export default function LandingHero() {
  return (
    <section className="min-h-screen bg-[var(--dvx-bg)] flex items-center transition-colors duration-300 pt-8 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
          >
            {/* Pill badge */}
            <div className="inline-flex items-center gap-2 border border-[var(--dvx-primary)] px-4 py-2 mb-8">
              <span className="w-2 h-2 rounded-full bg-[var(--dvx-primary)]" />
              <span className="font-oswald text-xs uppercase tracking-widest text-[var(--dvx-text)]">
                DVX Coaching · Premium Transformation System
              </span>
            </div>

            {/* Headline */}
            <h1 className="font-oswald text-5xl md:text-6xl font-bold text-[var(--dvx-text)] uppercase leading-none tracking-tight">
              Transform Your Body in{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[var(--dvx-primary)] to-[var(--dvx-primary-dark)]">
                12–16 Weeks
              </span>{" "}
              With Elite Personal Coaching
            </h1>

            <p className="mt-6 font-manrope text-[var(--dvx-text-muted)] text-lg leading-relaxed max-w-lg">
              Build muscle. Burn fat. Improve strength. Train with structure,
              accountability, and a premium coaching system built by Coach
              Vishal Duhan.
            </p>

            {/* Trust pills */}
            <div className="mt-6 flex flex-wrap gap-3">
              {[
                "20+ client transformations",
                "Strength & physique specialist",
                "Premium coaching experience",
              ].map((t) => (
                <span
                  key={t}
                  className="border border-[var(--dvx-border)] text-[var(--dvx-text-muted)] font-manrope text-sm px-4 py-2"
                >
                  {t}
                </span>
              ))}
            </div>

            {/* CTAs */}
            <div className="mt-10 flex flex-wrap gap-4 items-center">
              <Link to="/contact">
                <button className="px-8 py-4 bg-gradient-to-r from-[var(--dvx-primary)] to-[var(--dvx-primary-dark)] text-white font-oswald font-bold uppercase tracking-widest hover:scale-105 transition-transform flex items-center gap-2">
                  Apply For Coaching <ArrowRight size={18} />
                </button>
              </Link>
              <a href="/transformations">
                <button className="px-8 py-4 border border-[var(--dvx-border)] text-[var(--dvx-text)] font-oswald font-bold uppercase tracking-widest hover:border-[var(--dvx-primary)] hover:text-[var(--dvx-primary)] transition-colors">
                  See Client Results
                </button>
              </a>
            </div>
            <p className="mt-4 text-xs text-[var(--dvx-text-muted)] font-manrope">
              Limited coaching spots available each month.
            </p>
          </motion.div>

          {/* Right — Transformation preview card */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
          >
            <div className="bg-[var(--dvx-surface)] border border-[var(--dvx-border)] p-6 transition-colors duration-300">
              {/* Card header */}
              <div className="flex items-center justify-between mb-5">
                <div>
                  <div className="font-oswald text-xs tracking-widest text-[var(--dvx-primary)] uppercase mb-1">
                    D V X
                  </div>
                  <div className="font-oswald text-xl font-bold text-[var(--dvx-text)] uppercase">
                    Transformation Preview
                  </div>
                </div>
                <div className="text-right">
                  {/* <div className="text-xs text-[var(--dvx-text-muted)] font-manrope mb-1">
                    Current Offer
                  </div> */}
                  <Link to="/contact">
                    <button className="bg-[var(--dvx-primary)] text-white font-oswald text-xs uppercase tracking-wider px-4 py-2 hover:opacity-90 transition-opacity">
                      Apply Now
                    </button>
                  </Link>
                </div>
              </div>

              {/* Before / After */}
              <div className="grid grid-cols-2 gap-4 mb-5">
                {/* BEFORE */}
                <div className="bg-[var(--dvx-bg)] border border-[var(--dvx-border)] p-3">
                  <div className="text-xs font-oswald uppercase tracking-widest text-[var(--dvx-text-muted)] mb-3">
                    Before
                  </div>

                  <div className="relative h-40 overflow-hidden">
                    {/* Image */}
                    <img
                      src="/images/before.webp"
                      alt="Before"
                      className="absolute inset-0 w-full h-full object-cover"
                    />

                    {/* Grey bottom fade */}
                    <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/70 to-transparent" />

                    {/* Label */}
                    <div className="absolute bottom-2 left-2 text-[10px] text-white/70 font-manrope">
                      No structure
                    </div>
                  </div>

                  <p className="mt-3 text-xs text-[var(--dvx-text-muted)] font-manrope leading-relaxed">
                    Inconsistent routine, no clear plan, low accountability.
                  </p>
                </div>

                {/* AFTER */}
                <div className="bg-[var(--dvx-bg)] border border-[var(--dvx-primary)] p-3">
                  <div className="text-xs font-oswald uppercase tracking-widest text-[var(--dvx-primary)] mb-3">
                    After
                  </div>

                  <div className="relative h-40 overflow-hidden">
                    {/* Image */}
                    <img
                      src="/images/after.webp"
                      alt="After"
                      className="absolute inset-0 w-full h-full object-cover"
                    />

                    {/* Orange bottom fade */}
                    <div className="absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-[var(--dvx-primary)]/90 via-[var(--dvx-primary)]/60 to-transparent" />

                    {/* Label */}
                    <div className="absolute bottom-2 left-2 text-[10px] text-white font-manrope">
                      DVX System
                    </div>
                  </div>

                  <p className="mt-3 text-xs text-[var(--dvx-text-muted)] font-manrope leading-relaxed">
                    Sharper physique, higher strength, structured coaching
                    system.
                  </p>
                </div>
              </div>

              {/* Stats row */}
              <div className="grid grid-cols-3 gap-0 border-t border-[var(--dvx-border)] pt-5">
                {[
                  { val: "12", label: "Weeks" },
                  { val: "1:1", label: "Coaching" },
                  { val: "100%", label: "Customized" },
                ].map((s, i) => (
                  <div
                    key={i}
                    className={`text-center ${i < 2 ? "border-r border-[var(--dvx-border)]" : ""}`}
                  >
                    <div className="font-oswald text-2xl font-bold text-[var(--dvx-primary)]">
                      {s.val}
                    </div>
                    <div className="text-xs text-[var(--dvx-text-muted)] font-manrope mt-1">
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
